import { Box, Chip, FormControl, ListItemButton, Select, Stack, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import React, { useState } from 'react';

import { TextFieldComponent } from '../text-field/text-field-component';
import { TextFieldError, TextFieldTitle } from '../text-field/TextFieldTitle';
import { colors } from '../theme/theme';

import { CustomSelectWrapper } from './CommonSelectStyles';

interface CustomMultiSelectProps {
    value: string[];
    onChange: (value: string[]) => void;
    options: string[] | Array<{ id: string; name: string }>;
    placeholder?: string;
    selectMaxWidth?: string | number;
    selectMinWidth?: string | number;
    maxHeight?: string | number;
    iconColor?: string;
    title?: string;
    required?: boolean;
    errorValue?: string;
    helperTitle?: string;
    helperTitleOnClick?: () => void;
    sx?: SxProps;
    searchable?: boolean;
    searchPlaceholder?: string;
    noOptionMessage?: string;
    showSelectAll?: boolean;
}

const SelectIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
    <span
        {...props}
        className={['icon-down-caret', props.className].filter(Boolean).join(' ')}
        style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, ...props.style, right: '12px' }}
    />
);

export const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
    value = [],
    onChange,
    options,
    placeholder = 'Select',
    maxHeight = 250,
    title,
    required = false,
    errorValue = '',
    helperTitle,
    helperTitleOnClick,
    sx,
    searchable = false,
    searchPlaceholder = 'Search',
    noOptionMessage = 'No contracts found',
    showSelectAll = false,
}) => {
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);

    // Check if options are objects with id and name
    const isObjectOptions =
        options.length > 0 && typeof options[0] === 'object' && 'id' in options[0] && 'name' in options[0];

    // Get option value (id or string)
    const getOptionValue = (item: string | { id: string; name: string }) => (typeof item === 'object' ? item.id : item);

    // Get option label (name or string)
    const getOptionLabel = (item: string | { id: string; name: string }) =>
        typeof item === 'object' ? item.name : item;

    // Get label for a value
    const getLabelForValue = (val: string) => {
        if (isObjectOptions) {
            const option = (options as Array<{ id: string; name: string }>).find(opt => opt.id === val);
            return option ? option.name : val;
        }
        return val;
    };

    // Handle selection toggle
    const handleToggle = (itemValue: string) => {
        const currentIndex = value.indexOf(itemValue);
        const newValue = [...value];

        if (currentIndex === -1) {
            newValue.push(itemValue);
        } else {
            newValue.splice(currentIndex, 1);
        }

        onChange(newValue);
    };

    // Handle chip delete
    const handleDelete = (itemValue: string) => {
        const newValue = value.filter(v => v !== itemValue);
        onChange(newValue);
    };

    // Filter options based on search query
    const filteredOptions = searchQuery
        ? (options as Array<string | { id: string; name: string }>).filter(item => {
              const itemLabel = getOptionLabel(item);
              return itemLabel.toLowerCase().includes(searchQuery.toLowerCase());
          })
        : options;

    // Check if an item is selected

    // Handle Select All
    const handleSelectAll = () => {
        const allValues = options.map(item => getOptionValue(item));
        onChange(allValues);
    };

    // Handle Deselect All
    const handleDeselectAll = () => {
        onChange([]);
    };

    // Check if all options are selected
    const isAllSelected = options.length > 0 && value.length === options.length;
    const isSelected = (itemValue: string) => value.includes(itemValue);

    return (
        <CustomSelectWrapper>
            {title && <TextFieldTitle title={title} required={required} />}
            <FormControl>
                <Select
                    value={[]}
                    multiple
                    displayEmpty
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => {
                        setOpen(false);
                        setSearchQuery('');
                    }}
                    IconComponent={SelectIcon}
                    renderValue={() => (
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ lineHeight: '21px' }}>
                            {placeholder}
                        </Typography>
                    )}
                    sx={sx}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: colors.fullWhite,
                                mt: 1,
                                borderRadius: theme.spacing(1),
                                boxShadow: theme.shadows[2],
                                border: `1px solid ${colors.greyPreset[30]}`,
                                overflow: 'hidden',
                                pb: 1,
                                '& .MuiList-root': {
                                    gap: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: maxHeight,
                                    overflow: 'auto',
                                    py: 0,
                                },
                            },
                        },
                    }}
                >
                    <Stack gap={1} maxHeight={260}>
                        {searchable && (
                            <Stack
                                sx={{
                                    px: 1.5,
                                    pt: 1,
                                    position: 'sticky',
                                    top: 0,
                                    bgcolor: colors.fullWhite,
                                    zIndex: 1,
                                }}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                            >
                                <TextFieldComponent
                                    value={searchQuery}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    placeholder={searchPlaceholder}
                                    size="small"
                                    isAutoFocus
                                    startIcon={
                                        <span
                                            className="icon-search"
                                            style={{ fontSize: 16, color: theme.palette.text.secondary }}
                                        />
                                    }
                                />
                            </Stack>
                        )}
                        {showSelectAll && !searchQuery && (
                            <ListItemButton
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isAllSelected) {
                                        handleDeselectAll();
                                    } else {
                                        handleSelectAll();
                                    }
                                }}
                                selected={isAllSelected}
                                sx={{
                                    ...theme.typography.body2,
                                    color: isAllSelected ? colors.primaryLight[100] : colors.fullBlack,
                                    py: 1.5,
                                    px: 2.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: isAllSelected ? colors.primaryLight[5] : 'transparent',
                                    '&:hover': {
                                        backgroundColor: colors.primaryLight[15],
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: `${colors.primaryLight[5]} !important`,
                                    },
                                    borderBottom: `1px solid ${colors.greyPreset[30]}`,
                                    fontWeight: 600,
                                }}
                            >
                                <Typography variant="body2" color="textSecondary" fontWeight={600}>
                                    Select All
                                </Typography>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        border: `1.8px solid ${colors.successPreset[10]}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {isAllSelected && (
                                        <span
                                            className="icon-check"
                                            style={{
                                                fontSize: 10,
                                                color: colors.successPreset[10],
                                                fontWeight: 900,
                                            }}
                                        />
                                    )}
                                </Box>
                            </ListItemButton>
                        )}
                        {filteredOptions?.map((item: string | { id: string; name: string }) => {
                            const itemValue = getOptionValue(item);
                            const itemLabel = getOptionLabel(item);
                            const selected = isSelected(itemValue);

                            return (
                                <ListItemButton
                                    key={itemValue}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggle(itemValue);
                                    }}
                                    selected={selected}
                                    sx={{
                                        ...theme.typography.body2,
                                        color: selected ? colors.primaryLight[100] : colors.fullBlack,
                                        py: 1.5,
                                        px: 2.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        bgcolor: selected ? colors.primaryLight[5] : 'transparent',
                                        '&:hover': {
                                            backgroundColor: colors.primaryLight[15],
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.primaryLight[5]} !important`,
                                        },
                                    }}
                                >
                                    <Typography variant="body2" color="textPrimary">
                                        {itemLabel}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            border: `1.8px solid ${colors.successPreset[10]}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {selected && (
                                            <span
                                                className="icon-check"
                                                style={{
                                                    fontSize: 10,
                                                    color: colors.successPreset[10],
                                                    fontWeight: 900,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </ListItemButton>
                            );
                        })}
                        {filteredOptions?.length === 0 && searchQuery && (
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                    py: 2,
                                    px: 2.5,
                                    color: colors.greyPreset[60],
                                }}
                            >
                                {noOptionMessage}
                            </Typography>
                        )}
                        {filteredOptions?.length === 0 && !searchQuery && (
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                    py: 2,
                                    px: 2.5,
                                    color: colors.greyPreset[60],
                                }}
                            >
                                {noOptionMessage}
                            </Typography>
                        )}
                    </Stack>
                </Select>
            </FormControl>

            {/* Selected items as chips */}
            {value.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
                    {value.map(val => (
                        <Chip
                            key={val}
                            label={getLabelForValue(val)}
                            onDelete={() => handleDelete(val)}
                            deleteIcon={
                                <span
                                    className="icon-close-circle"
                                    style={{
                                        fontSize: 14,
                                        paddingLeft: 3,
                                        fontWeight: 600,
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            }
                            sx={{
                                ...theme.typography.body2,
                                bgcolor: colors.greyPreset[90],
                                border: `1px solid ${colors.greyPreset[30]}`,
                                borderRadius: 2,
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                '& .MuiChip-label': {
                                    px: 1,
                                },
                                '& .MuiChip-deleteIcon': {
                                    mr: 1,
                                    '&:hover': {
                                        opacity: 0.7,
                                    },
                                },
                            }}
                        />
                    ))}
                </Stack>
            )}

            {errorValue && <TextFieldError errorValue={errorValue} />}
            {helperTitle && (
                <Box onClick={helperTitleOnClick} sx={{ cursor: 'pointer' }}>
                    <Typography variant="subtitle2" className="helperTitle">
                        {helperTitle}
                    </Typography>
                </Box>
            )}
        </CustomSelectWrapper>
    );
};
